"use client";

import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { productSlug } from "@package/util/utils/productSlug";
import { formatCurrencyAmount } from "@package/util/utils/currency";
import styles from "./SummerFavourites.module.css";

const AUTO_SLIDE_DELAY_MS = 6500;

const summerProducts = [
    {
        id: 1,
        brand: "Derma",
        name: "AiryGlow Gel Sunscreen SPF 60",
        image: "/assets/images/product-1.png",
        originalPrice: 300.00,
        salePrice: 100.00,
        rating: 5,
        isNew: false,
    },
    {
        id: 2,
        brand: "Loreal",
        name: "AspireDew Calming Face Oil",
        image: "/assets/images/product-2.png",
        originalPrice: 300.00,
        salePrice: 100.00,
        rating: 5,
        isNew: true,
    },
    {
        id: 3,
        brand: "Aqua",
        name: "Aqualogia",
        image: "/assets/images/product-3.png",
        originalPrice: 300.00,
        salePrice: 100.00,
        rating: 5,
        isNew: true,
    },
    {
        id: 4,
        brand: "SolarVive",
        name: "AquaLuxe Hydrating Face Oil",
        image: "/assets/images/product-4.png",
        originalPrice: 300.00,
        salePrice: 100.00,
        rating: 5,
        isNew: true,
    },
];

function parsePositiveNumber(rawValue) {
    if (rawValue === null || rawValue === undefined) return null;
    if (typeof rawValue === "number") {
        return Number.isFinite(rawValue) && rawValue > 0 ? rawValue : null;
    }

    const numericValue = Number(String(rawValue).replace(/[^0-9.]/g, ""));
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
}

function formatPrice(rawValue) {
    return formatCurrencyAmount(parsePositiveNumber(rawValue) || 0, "enterprise");
}

function buildAssetUrl(path, productBaseUrl) {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;

    const baseUrl = productBaseUrl || process.env.NEXT_PUBLIC_SECURED_URL || "";
    if (!baseUrl) return path;

    return `${baseUrl.replace(/\/+$/, "")}/${String(path).replace(/^\/+/, "")}`;
}

function getProductImage(product, productBaseUrl) {
    return buildAssetUrl(
        product?.imageThumbnail ||
        product?.imageThumbNail ||
        product?.selectedImageThumbKey ||
        product?.image ||
        product?.thumbnail ||
        product?.imageUrl ||
        product?.image_url,
        productBaseUrl,
    );
}

function getCategoryLabel(product) {
    const firstCategory = Array.isArray(product?.categories)
        ? product.categories[0]
        : null;

    return (
        product?.brand ||
        product?.brandName ||
        product?.categoryName ||
        product?.productCategory ||
        firstCategory?.label ||
        firstCategory?.categoryName ||
        firstCategory?.name ||
        "ENT Central"
    );
}

function getOriginalPrice(product, salePrice) {
    const originalPrice = parsePositiveNumber(
        product?.originalPrice ??
        product?.actualPrice ??
        product?.regularPrice ??
        product?.mrp ??
        product?.strikePrice ??
        product?.compareAtPrice,
    );

    return originalPrice && originalPrice > salePrice ? originalPrice : null;
}

function mapApiProduct(product, index, productBaseUrl) {
    const salePrice =
        parsePositiveNumber(
            product?.salePrice ??
            product?.discountPrice ??
            product?.price ??
            product?.sellingPrice ??
            product?.distributionPrice,
        ) || 0;

    return {
        id: product?.productId || product?.id || product?.slug || index,
        brand: getCategoryLabel(product),
        name: product?.name || product?.productName || "Product",
        image: getProductImage(product, productBaseUrl) || "/assets/images/product-1.png",
        originalPrice: getOriginalPrice(product, salePrice),
        salePrice,
        rating: Math.max(
            1,
            Math.min(
                5,
                Math.round(parsePositiveNumber(product?.averageRating || product?.rating) || 5),
            ),
        ),
        isNew: Boolean(product?.isNew || product?.newArrival || product?.isNewArrival),
        href: `/product/${productSlug(product?.name || product?.productName || "product")}/${product?.productId || product?.id || index}`,
    };
}

export default function SummerFavourites({ products = [], productBaseUrl = "" }) {
    const viewportRef = useRef(null);
    const firstCardRef = useRef(null);
    const autoSlideTimerRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [cardStep, setCardStep] = useState(304);
    const [visibleCards, setVisibleCards] = useState(1);
    const productsToRender = useMemo(() => {
        const apiProducts = Array.isArray(products) ? products.filter(Boolean) : [];

        if (!apiProducts.length) {
            return summerProducts;
        }

        return apiProducts.map((product, index) =>
            mapApiProduct(product, index, productBaseUrl),
        );
    }, [productBaseUrl, products]);

    const maxIndex = Math.max(0, productsToRender.length - visibleCards);

    useEffect(() => {
        const measureCarousel = () => {
            const viewport = viewportRef.current;
            const firstCard = firstCardRef.current;
            if (!viewport || !firstCard) return;

            const cardRect = firstCard.getBoundingClientRect();
            const viewportStyles = window.getComputedStyle(viewport);
            const trackGap = parseFloat(viewportStyles.getPropertyValue("--product-gap")) || 24;
            const nextCardStep = cardRect.width + trackGap;

            setCardStep(nextCardStep);
            setVisibleCards(Math.max(1, Math.floor((viewport.clientWidth + trackGap) / nextCardStep)));
        };

        measureCarousel();
        window.addEventListener("resize", measureCarousel);

        return () => window.removeEventListener("resize", measureCarousel);
    }, [productsToRender.length]);

    useEffect(() => {
        setActiveIndex((currentIndex) => Math.min(currentIndex, maxIndex));
    }, [maxIndex]);

    const moveCarousel = useCallback((direction) => {
        setActiveIndex((currentIndex) => {
            if (direction === "left") {
                return currentIndex <= 0 ? maxIndex : currentIndex - 1;
            }

            return currentIndex >= maxIndex ? 0 : currentIndex + 1;
        });
    }, [maxIndex]);

    useEffect(() => {
        if (maxIndex <= 0) return undefined;

        autoSlideTimerRef.current = window.setInterval(() => {
            moveCarousel("right");
        }, AUTO_SLIDE_DELAY_MS);

        return () => {
            if (autoSlideTimerRef.current) {
                window.clearInterval(autoSlideTimerRef.current);
            }
        };
    }, [maxIndex, moveCarousel]);

    return (
        <section className={styles.summerSection}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionLabel}>OUR SUMMER FAVOURITES</span>
                <h2 className={styles.sectionTitle}>GIVE YOUR SKIN THAT SUMMER GLOW</h2>
            </div>

            <div className={styles.carouselWrapper}>
                <button
                    className={styles.arrowButton}
                    onClick={() => moveCarousel("left")}
                    aria-label="Previous products"
                    type="button"
                >
                    ‹
                </button>

                <div className={styles.carouselViewport} ref={viewportRef}>
                    <div
                        className={styles.carouselTrack}
                        style={{ transform: `translate3d(-${activeIndex * cardStep}px, 0, 0)` }}
                    >
                        {productsToRender.map((product, productIndex) => (
                            <Link
                                key={`${product.id}-${productIndex}`}
                                href={product.href || "#"}
                                className={styles.productCard}
                                ref={productIndex === 0 ? firstCardRef : null}
                            >
                                <div className={styles.imageContainer}>
                                    {product.isNew && <span className={styles.newBadge}>New</span>}
                                    <img src={product.image} alt={product.name} className={styles.image} />
                                    <div className={styles.quickViewButton}>Quick View</div>
                                </div>
                                <div className={styles.productInfo}>
                                    <div className={styles.productMeta}>
                                        <h3 className={styles.brandName}>{product.brand}</h3>
                                        <div className={styles.ratingContainer}>
                                            {[...Array(product.rating)].map((_, i) => (
                                                <svg key={i} xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                                    <path d="M13 4.69733H8.07408L6.54875 0L4.99796 4.69733H0L4.04788 7.54379L2.5285 12.1691L6.51896 9.26629L10.5289 12.1415L9.00954 7.52213L13 4.69733Z" fill="#19497A" />
                                                </svg>
                                            ))}
                                        </div>
                                    </div>
                                    <p className={styles.productName}>{product.name}</p>
                                    <div className={styles.priceContainer}>
                                        {product.originalPrice ? (
                                            <span className={styles.originalPrice}>
                                                {formatPrice(product.originalPrice)}
                                            </span>
                                        ) : null}
                                        <span className={styles.salePrice}>{formatPrice(product.salePrice)}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                <button
                    className={styles.arrowButton}
                    onClick={() => moveCarousel("right")}
                    aria-label="Next products"
                    type="button"
                >
                    ›
                </button>
            </div>
        </section>
    );
}
